import { makeAutoObservable } from "mobx";
import { countActiveDashboardScopeFilters } from "../utils/dashboard-filter-options";

const DEFAULT_FILTERS = {
  years: [],
  frameworks: [],
  categories: [],
  hierarchy: "all",
  orgIds: [],
};

const DEFAULT_AVAILABLE = {
  years: [],
  reportingYears: [],
  frameworks: [],
  frameworkObjects: [],
  orgTree: [],
};

const ALL = "__all__";

const normalizeFrameworkFilter = (value) => {
  if (!Array.isArray(value)) return [];

  const frameworks = value.filter((item) => item && item !== ALL);

  return frameworks.length === value.length ? value : frameworks;
};

const areValuesEqual = (left, right) => {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((item, index) => item === right[index]);
  }

  return left === right;
};

export class DashboardFiltersStore {
  filters = { ...DEFAULT_FILTERS };
  available = { ...DEFAULT_AVAILABLE };
  loading = { boundary: false, orgs: false };
  frameworkAllSelected = false;

  constructor() {
    makeAutoObservable(this);
  }

  setFilter = (key, value) => {
    const nextValue =
      key === "frameworks" ? normalizeFrameworkFilter(value) : value;
    const currentValue = this.filters[key];

    if (
      areValuesEqual(currentValue, nextValue) &&
      !(key === "frameworks" && this.frameworkAllSelected)
    ) {
      return;
    }

    this.filters[key] = Array.isArray(nextValue) ? [...nextValue] : nextValue;
    if (key === "frameworks" && this.filters.frameworks.length > 0) {
      this.frameworkAllSelected = false;
    }
    // Switching the hierarchy lens invalidates any prior pick: the previous
    // ids (real or synthetic country:*) don't belong in the new picker.
    if (key === "hierarchy") this.filters.orgIds = [];
  };

  setFrameworkAllSelected = (value) => {
    this.frameworkAllSelected = Boolean(value);
  };

  setLoading = (key, value) => {
    this.loading[key] = Boolean(value);
  };

  setOrgTree = (tree) => {
    this.available.orgTree = Array.isArray(tree) ? tree : [];
  };

  setFrameworkObjects = (frameworks) => {
    this.available.frameworkObjects = Array.isArray(frameworks)
      ? [...frameworks]
      : [];
  };

  setAvailableYears = (years) => {
    this.available.years = Array.isArray(years) ? [...years] : [];
  };

  setReportingYears = (reportingYears) => {
    this.available.reportingYears = Array.isArray(reportingYears)
      ? [...reportingYears]
      : [];
  };

  setAvailableFrameworks = (frameworks) => {
    const frameworkObjects = Array.isArray(frameworks) ? [...frameworks] : [];
    const frameworkCodes = frameworkObjects
      .map((framework) => framework?.code)
      .filter(Boolean);

    this.available.frameworkObjects = frameworkObjects;
    this.available.frameworks = frameworkCodes;
    // Reassigning filters.frameworks with unchanged content would still
    // retrigger every reaction tracking it (new array reference), refiring
    // the dashboard queries with an identical payload on each options load.
    const nextSelected = this.filters.frameworks.filter((framework) =>
      frameworkCodes.includes(framework),
    );
    if (!areValuesEqual(this.filters.frameworks, nextSelected)) {
      this.filters.frameworks = nextSelected;
    }
  };

  setBoundary = ({ startYear, endYear, frameworkCodes }) => {
    const years = [];
    if (Number.isInteger(startYear) && Number.isInteger(endYear)) {
      for (let y = startYear; y <= endYear; y += 1) years.push(y);
    }
    this.available.years = years;
    if (
      Array.isArray(frameworkCodes) &&
      this.available.frameworks.length === 0
    ) {
      this.available.frameworks = [...frameworkCodes];
    }
  };

  get activeCount() {
    return countActiveDashboardScopeFilters(this.filters);
  }

  // One-time copy of another instance's filter values (deep enough that
  // later mutations never leak between instances). Used by Target Setup
  // Overview to start from the dashboard's current scope without sharing
  // the dashboard's store.
  seedFrom = (filters) => {
    this.filters = {
      years: [...(filters?.years ?? [])],
      frameworks: [...(filters?.frameworks ?? [])],
      categories: [...(filters?.categories ?? [])],
      hierarchy: filters?.hierarchy ?? "all",
      orgIds: [...(filters?.orgIds ?? [])],
    };
    this.frameworkAllSelected = false;
  };

  reset = () => {
    this.filters = { ...DEFAULT_FILTERS };
    this.frameworkAllSelected = false;
  };
}

// Module singleton owned by the ESG dashboard. Target Setup Overview keeps
// its own instance (target-setup/stores/target-setup-filters.store.js) —
// nothing outside the dashboard may mutate this one.
export const dashboardFiltersStore = new DashboardFiltersStore();
